import { EMPTY_TABLE_STATE, type SavedViewEntity, type TableState } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Check, Star } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type SavedViewCreate, type SavedViewRecord } from '../../lib/client.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';
import { Input } from '../ui/input.tsx';

/** The wire shape has no `undefined` members (exactOptionalPropertyTypes); a JSON round trip drops them. */
const toWire = (s: TableState) => JSON.parse(JSON.stringify(s)) as SavedViewCreate['state'];

export const SAVED_VIEWS_QUERY = (entity: string) => ['saved-views', entity] as const;

/**
 * Personal saved views (RELAY-942). A view is the table state under a name, owned by the
 * user who saved it; the menu lists them, applies one, saves the current state as a new one
 * or over the active one, marks a default (applied when the list opens with nothing in the
 * URL) and deletes. `activeId` is whichever view the current state was last loaded from, so
 * "Update" knows its target; any change to the state keeps the id (the view is "modified"
 * until updated), which is what a spreadsheet user expects.
 */
export function ViewsMenu({
  entity,
  state,
  activeId,
  onApply,
}: {
  entity: SavedViewEntity;
  state: TableState;
  activeId: string | null;
  onApply: (state: TableState, viewId: string | null) => void;
}) {
  const qc = useQueryClient();
  const key = SAVED_VIEWS_QUERY(entity);
  const views = useQuery({ queryKey: key, queryFn: () => api.savedViews.list(entity) });
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: (input: { name: string }) =>
      api.savedViews.create({ entity, name: input.name, state: toWire(state), isDefault: false }),
    onSuccess: async (view) => {
      setSaving(false);
      setName('');
      await invalidate();
      onApply(view.state, view.id);
    },
    onError: (err) => setError(describeError(err)),
  });
  const update = useMutation({
    mutationFn: (v: SavedViewRecord) => api.savedViews.patch(v.id, { state: toWire(state) }),
    onSuccess: invalidate,
    onError: (err) => setError(describeError(err)),
  });
  const setDefault = useMutation({
    mutationFn: (v: SavedViewRecord) => api.savedViews.patch(v.id, { isDefault: !v.isDefault }),
    onSuccess: invalidate,
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (v: SavedViewRecord) => api.savedViews.remove(v.id),
    onSuccess: async (_r, v) => {
      await invalidate();
      if (v.id === activeId) onApply(EMPTY_TABLE_STATE, null);
    },
    onError: (err) => setError(describeError(err)),
  });

  const list = views.data ?? [];
  const active = list.find((v) => v.id === activeId);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="views-menu">
            <Bookmark /> {active ? active.name : 'Views'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {list.length === 0 && (
            <div className="text-muted-foreground px-2 py-1.5 text-sm">No saved views yet.</div>
          )}
          {list.map((v) => (
            <DropdownMenuItem
              key={v.id}
              onSelect={() => onApply(v.state, v.id)}
              data-testid={`view-${v.id}`}
            >
              {v.id === activeId ? <Check className="size-4" /> : <span className="size-4" />}
              <span className="flex-1 truncate">{v.name}</span>
              {v.isDefault && <Star className="text-brand size-3.5" aria-label="Default view" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSaving(true)} data-testid="view-save-as">
            Save current as…
          </DropdownMenuItem>
          {active && (
            <>
              <DropdownMenuItem onSelect={() => update.mutate(active)} data-testid="view-update">
                Update “{active.name}”
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setDefault.mutate(active)}
                data-testid="view-default"
              >
                {active.isDefault ? 'Clear default' : 'Make default'}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => remove.mutate(active)} data-testid="view-delete">
                Delete “{active.name}”
              </DropdownMenuItem>
            </>
          )}
          {activeId && (
            <DropdownMenuItem
              onSelect={() => onApply(EMPTY_TABLE_STATE, null)}
              data-testid="view-reset"
            >
              Reset to default columns
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {error && !saving && (
        <span className="text-danger text-sm" role="alert">
          {error}
        </span>
      )}
      <Dialog open={saving} onOpenChange={setSaving}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <DialogDescription>
              Columns, filter, sort and search, under a name only you see.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (name.trim()) create.mutate({ name: name.trim() });
            }}
          >
            <Input
              aria-label="View name"
              placeholder="e.g. Bookkeepers in Colombia"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            {error && (
              <p className="text-danger text-sm" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setSaving(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!name.trim() || create.isPending}
                data-testid="view-save"
              >
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
